import {
  XBOT_MESSAGE_VERSION, XbotAcceptedEvent, XbotCancelledEvent, XbotCancelReason, XbotMessage, XbotMessageBlock,
  XbotMessageState, XbotStatusEvent, XbotStatusPhase, XbotStreamEvent,
} from "@dto/pyxbot-agent.dto";
import { Label } from "src/data/Label";

// Streamed events (accepted, then status/block any number of times, then done/cancelled/error) are folded in
// order; duplicates or events from another turn are ignored, and a stream that ends early reads as unfinished.

export type XbotTurnOutcome = "running" | "done" | "cancelled" | "error" | "unfinished";

export interface XbotTurnProgress {
  outcome: XbotTurnOutcome;
  /** What the turn is doing now; only while it runs. */
  phase: XbotStatusEvent | null;
  cancelled: XbotCancelledEvent | null;
  lastSeq: number;
  /** When this page learned the turn had started (its own clock, ms), for the time the progress bar shows. */
  startedAt?: number;
  /** The step sentences said so far, oldest first, each once in a row; the last is the step now. */
  steps?: string[];
}

// Step sentences ("Finding db01…") come from Xbot's code, never the model; a step without one falls back to its phase.

export const PHASE_WORDS: Record<XbotStatusPhase, string> = {
  reading: "Reading the question…",
  calling_tool: `Looking it up in ${Label.XORMON_NG}…`,
  waiting_ng: `Waiting for ${Label.XORMON_NG} to answer…`,
  composing: "Putting the answer together…",
  condensing: "Tidying up the conversation…",
};

export function stepText(phase: XbotStatusEvent | null | undefined): string {
  const note = typeof phase?.note === "string" ? phase.note.trim() : "";
  if (note) {
    return note;
  }
  return (phase && PHASE_WORDS[phase.phase]) || "Working on it…";
}

/** The steps with the newest one after them, unless it is the one said last. */
export function withStep(steps: readonly string[] | undefined, step: string): string[] {
  const before = steps ?? [];
  return before[before.length - 1] === step ? [...before] : [...before, step];
}

/** The code a stopped answer keeps its reason under (settledMessage), as the reason the service gave. */
export const STOPPED_CODE = "stopped";

// A stop is said as its head ("Stopped") and its reason under it.
export const STOP_REASON: Record<XbotCancelReason, string> = {
  replaced: "A newer question took its place.",
  closed: "The chat was closed.",
  timeout: "The answer took too long.",
  budget: "The answer reached its work limit.",
};

/** What a stop says when its reason is not one of those. */
export const STOPPED_SENTENCE = "This answer was stopped.";

/** Why a settled answer was stopped: from the turn while it runs, from the answer once it is kept; null if unknown. */
export function stopReasonOf(message: XbotMessage | null | undefined, cancelled: XbotCancelledEvent | null | undefined): XbotCancelReason | null {
  if (cancelled?.reason) {
    return cancelled.reason;
  }
  const error = message?.error;
  const kept = error?.code === STOPPED_CODE ? (error.detail as { reason?: unknown } | undefined)?.reason : undefined;
  return typeof kept === "string" && Object.hasOwn(STOP_REASON, kept) ? kept as XbotCancelReason : null;
}

/** What an answer whose stream ended before the turn did says, here and when it is read back later. */
export const UNFINISHED_SENTENCE = "The answer stopped before it was finished. Ask again to get the rest.";

/** The code such an answer is kept under once it settles (settledMessage), so it reads back as unfinished. */
export const UNFINISHED_CODE = "unfinished";

export interface XbotTurn {
  message: XbotMessage;
  progress: XbotTurnProgress;
}

export function outcomeOfState(state: XbotMessageState | null | undefined): XbotTurnOutcome {
  if (state === "pending") {
    return "running";
  }
  if (state === "error" || state === "cancelled") {
    return state;
  }
  return "done";
}

// A turn that did not finish is stored as an error with UNFINISHED_CODE, but reads back as "unfinished", not a failure.
export function outcomeOfMessage(message: XbotMessage | null | undefined): XbotTurnOutcome {
  const outcome = outcomeOfState(message?.state);
  return outcome === "error" && message?.error?.code === UNFINISHED_CODE ? "unfinished" : outcome;
}

export function beginTurn(accepted: XbotAcceptedEvent, now: number = Date.now()): XbotTurn {
  return {
    message: {
      schema_version: XBOT_MESSAGE_VERSION,
      id: accepted.message_id,
      thread_id: accepted.thread_id,
      turn_seq: accepted.turn_seq,
      role: "assistant",
      state: "pending",
      handler: "agent",
      created_at: accepted.at,
      blocks: [],
      claims: [],
      trace: [],
      usage: null,
      standing: null,
      error: null,
      turn_ms: null,
    },
    progress: { outcome: "running", phase: null, cancelled: null, lastSeq: accepted.seq, startedAt: now },
  };
}

/** A block replaces the one with the same id where it stands; a new id is added at the end. */
export function withBlock(blocks: XbotMessageBlock[], block: XbotMessageBlock): XbotMessageBlock[] {
  const at = blocks.findIndex((one) => one.id === block.id);
  if (at < 0) {
    return [...blocks, block];
  }
  const next = blocks.slice();
  next[at] = block;
  return next;
}

/** Applies one event. Before the turn is accepted only the accepted event starts it. */
export function applyEvent(turn: XbotTurn | null, event: XbotStreamEvent): XbotTurn | null {
  if (!turn) {
    return event.event === "accepted" ? beginTurn(event) : null;
  }
  const { message, progress } = turn;
  const ownTurn = event.thread_id === message.thread_id && event.turn_seq === message.turn_seq;
  if (!ownTurn || event.seq <= progress.lastSeq || progress.outcome !== "running") {
    return turn;
  }
  const seen: XbotTurnProgress = { ...progress, lastSeq: event.seq };
  switch (event.event) {
    case "status":
      return { message, progress: { ...seen, phase: event, steps: withStep(progress.steps, stepText(event)) } };
    case "block":
      return event.block
        ? { message: { ...message, blocks: withBlock(message.blocks, event.block) }, progress: seen }
        : { message, progress: seen };
    case "done": {
      const done = event.message ?? message;
      const outcome = outcomeOfState(done.state);
      return { message: done, progress: { ...seen, phase: null, outcome: outcome === "running" ? "unfinished" : outcome } };
    }
    case "cancelled":
      return {
        message: { ...message, state: "cancelled" },
        progress: { ...seen, phase: null, outcome: "cancelled", cancelled: event },
      };
    case "error":
      return {
        message: { ...message, state: "error", error: event.error ?? null },
        progress: { ...seen, phase: null, outcome: "error" },
      };
    default:
      return { message, progress: seen };
  }
}

/** The stream closed. A turn still running then did not finish. */
export function endOfStream(turn: XbotTurn | null): XbotTurn | null {
  if (turn?.progress.outcome !== "running") {
    return turn;
  }
  return { message: turn.message, progress: { ...turn.progress, phase: null, outcome: "unfinished" } };
}

// The message stored after a turn settles, with its outcome folded into its state; null while still running.
export function settledMessage(turn: XbotTurn | null): XbotMessage | null {
  if (!turn || turn.progress.outcome === "running") {
    return null;
  }
  const { message, progress } = turn;
  if (progress.outcome === "unfinished") {
    return { ...message, state: "error", error: message.error ?? { code: UNFINISHED_CODE, message: UNFINISHED_SENTENCE } };
  }
  if (progress.outcome === "cancelled") {
    // the reason is kept with the answer, so a stop still says why (and what to try) once the turn has settled; the
    // message is the sentence the card says, since the history and a copied answer read it (answerText), not the code
    const reason = progress.cancelled?.reason;
    const said = reason && Object.hasOwn(STOP_REASON, reason) ? STOP_REASON[reason] : STOPPED_SENTENCE;
    return { ...message, state: "cancelled", error: reason ? { code: STOPPED_CODE, message: said, detail: { reason } } : message.error };
  }
  if (progress.outcome === "error") {
    return { ...message, state: "error" };
  }
  return message;
}
